import { COMPOUND_GROUPS } from "../private/gamePropsConfig";

// Some rule definitions apply to a set of related outcomes (e.g. the 9
// halftime/fulltime combinations) and let the user pick which ones. The
// backend rule definition doesn't describe those options, so they're
// configured here, matched by the definition's description. The selection is
// saved in rule details under `detailKey` as an array of option values.
export interface RuleOptionSelector {
  matches: (description: string) => boolean;
  detailKey: string;
  prompt: string;
  options: { value: string; label: string }[];
  columns: number;
}

export const RULE_OPTION_SELECTORS: RuleOptionSelector[] = [
  {
    matches: (description) => /half\s*time\s*\/?\s*full\s*time/i.test(description),
    detailKey: "outcomes",
    prompt: "Apply this rule to these Halftime / Fulltime results:",
    options: COMPOUND_GROUPS.halftime_fulltime.members.map((m) => ({
      value: m.base,
      label: m.rowLabel,
    })),
    columns: 3,
  },
];

export function findRuleOptionSelector(
  description: string,
): RuleOptionSelector | undefined {
  return RULE_OPTION_SELECTORS.find((s) => s.matches(description));
}

// Resolves a saved rule's option selection for display, matched the same way
// WatcherFormRule matches it while building the rule (group + definition
// description, both filled into `details` by the backend). Rules saved
// before this field existed have no `detailKey` array — treated as "every
// option" since that's how the backend behaves for them.
export function getRuleOptionSelection(
  details: Record<string, unknown>,
): { selector: RuleOptionSelector; selected: string[] } | undefined {
  const description = `${(details["rule_group_description"] as string) ?? ""} ${
    (details["rule_definition_description"] as string) ?? ""
  }`;
  const selector = findRuleOptionSelector(description);
  if (!selector) return undefined;
  const saved = details[selector.detailKey];
  const selected = Array.isArray(saved)
    ? (saved as string[])
    : selector.options.map((o) => o.value);
  return { selector, selected };
}
